"use client";

import { useCallback, useEffect, useState } from "react";
import { HiChevronDown } from "@/components/icons";

import { extensionDataPractices, families, type ExtensionFamily } from "../_data";

const FAMILY_KEYS = Object.keys(families) as ExtensionFamily[];

export default function ExtensionDataPractices() {
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  // On mount: if the URL has a hash that matches an extension slug, open
  // that block and scroll to it. This is the deep-link entry point for
  // CWS reviewers who paste a per-extension anchor URL.
  //
  // We must read window.location.hash AFTER mount (it's not available
  // during SSR) and then set state — this is the canonical "subscribe to
  // a browser-only global" pattern. The react-hooks/set-state-in-effect
  // rule flags it because it can't see that the effect reads an external
  // source. The setState is conditional + one-shot (empty deps), so there
  // is no re-render loop.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const match = extensionDataPractices.find((e) => e.slug === hash);
    if (!match) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpenSlug(match.slug);
    // Defer the scroll so the just-opened block has rendered its
    // collapsed/expanded layout before the browser computes the target
    // offset. Without this, the scroll lands above the card.
    requestAnimationFrame(() => {
      const el = document.getElementById(match.slug);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const handleJump = useCallback((slug: string) => {
    setOpenSlug((prev) => (prev === slug ? prev : slug));
    // Update the URL hash without triggering Next.js navigation
    if (typeof window !== "undefined") {
      history.replaceState(null, "", `#${slug}`);
    }
    requestAnimationFrame(() => {
      const el = document.getElementById(slug);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const handleToggle = useCallback((slug: string) => {
    setOpenSlug((prev) => (prev === slug ? null : slug));
  }, []);

  return (
    <>
      {/* Sticky chip bar — sits below the site header (top-20) and inside
          the per-extension section card so its backdrop blends with the
          card background. */}
      <div className="sticky top-20 z-30 -mx-2 sm:-mx-4 mb-8 px-3 sm:px-4 py-3 rounded-xl bg-card/95 backdrop-blur-md border border-border/60 shadow-sm">
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Jump to extension
          </span>
          <span className="text-[10px] text-muted-foreground">
            {extensionDataPractices.length} extensions
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {extensionDataPractices.map((ext) => {
            const family = families[ext.family];
            const isActive = openSlug === ext.slug;
            return (
              <button
                key={ext.slug}
                type="button"
                onClick={() => handleJump(ext.slug)}
                aria-current={isActive ? "true" : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all hover:translate-y-[-1px] ${
                  isActive ? family.chipActive : family.chip
                }`}
              >
                <span aria-hidden="true" className="text-sm leading-none">
                  {family.emoji}
                </span>
                <span className="font-semibold">{ext.name.replace(/ Pro$/, "")}</span>
              </button>
            );
          })}
        </div>
        {/* Family legend */}
        <div className="mt-3 pt-3 border-t border-border/40 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
          {FAMILY_KEYS.map((key) => {
            const f = families[key];
            return (
              <span key={key} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={`inline-block size-2 rounded-full ${f.cardStripe}`}
                />
                <span>{f.label}</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Per-extension cards */}
      <div className="space-y-6">
        {extensionDataPractices.map((ext) => {
          const family = families[ext.family];
          const isOpen = openSlug === ext.slug;
          return (
            <article
              key={ext.slug}
              id={ext.slug}
              className="scroll-mt-44 rounded-xl border border-border bg-background/60 overflow-hidden transition-shadow hover:shadow-sm"
            >
              {/* Family color stripe */}
              <div className={`h-1 ${family.cardStripe}`} aria-hidden="true" />
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${family.chip}`}
                  >
                    <span aria-hidden="true">{family.emoji}</span>
                    {family.label}
                  </span>
                </div>
                <h4 className="text-xl font-bold mb-3 text-foreground">{ext.name}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{ext.summary}</p>

                <button
                  type="button"
                  onClick={() => handleToggle(ext.slug)}
                  aria-expanded={isOpen}
                  aria-controls={`${ext.slug}-details`}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card hover:bg-accent hover:border-primary/40 transition-colors px-4 py-2 text-sm font-medium text-foreground"
                >
                  <HiChevronDown
                    className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                  {isOpen ? "Hide" : "Show"} full data flow
                </button>

                {isOpen && (
                  <div
                    id={`${ext.slug}-details`}
                    className="mt-6 space-y-6 border-t border-border pt-6"
                  >
                    {/* Stored */}
                    {ext.stored.length > 0 && (
                      <div>
                        <h5 className="text-sm font-bold text-foreground mb-3">
                          Stored on your device
                        </h5>
                        <div className="space-y-3">
                          {ext.stored.map((s, i) => (
                            <div
                              key={i}
                              className="rounded-lg border border-border/50 bg-muted/30 p-4 text-sm"
                            >
                              <p className="font-semibold text-foreground mb-1">{s.what}</p>
                              <ul className="text-xs text-muted-foreground space-y-1">
                                <li>
                                  <strong className="text-foreground/80">Where:</strong> {s.where}
                                </li>
                                <li>
                                  <strong className="text-foreground/80">Purpose:</strong>{" "}
                                  {s.purpose}
                                </li>
                                <li>
                                  <strong className="text-foreground/80">Retention:</strong>{" "}
                                  {s.retention}
                                </li>
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Shared */}
                    {ext.shared.length > 0 && (
                      <div>
                        <h5 className="text-sm font-bold text-foreground mb-3">
                          Transmitted to third parties
                        </h5>
                        <div className="space-y-3">
                          {ext.shared.map((s, i) => (
                            <div
                              key={i}
                              className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4 text-sm"
                            >
                              <p className="font-semibold text-foreground mb-2">→ {s.recipient}</p>
                              <ul className="text-xs text-muted-foreground space-y-1">
                                <li>
                                  <strong className="text-foreground/80">What:</strong> {s.what}
                                </li>
                                <li>
                                  <strong className="text-foreground/80">When:</strong> {s.when}
                                </li>
                                <li>
                                  <strong className="text-foreground/80">Purpose:</strong>{" "}
                                  {s.purpose}
                                </li>
                                {s.note && (
                                  <li>
                                    <strong className="text-foreground/80">Note:</strong> {s.note}
                                  </li>
                                )}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Not shared */}
                    {ext.notShared.length > 0 && (
                      <div>
                        <h5 className="text-sm font-bold text-foreground mb-3">
                          Explicitly NOT shared / NOT sold
                        </h5>
                        <ul className="space-y-2 text-xs text-muted-foreground list-disc list-outside ml-5">
                          {ext.notShared.map((n, i) => (
                            <li key={i}>{n}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Your controls */}
                    {ext.yourControls.length > 0 && (
                      <div>
                        <h5 className="text-sm font-bold text-foreground mb-3">Your controls</h5>
                        <ul className="space-y-2 text-xs text-muted-foreground list-disc list-outside ml-5">
                          {ext.yourControls.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Third-party links */}
                    {ext.thirdPartyLinks.length > 0 && (
                      <div>
                        <h5 className="text-sm font-bold text-foreground mb-3">
                          Third-party services referenced above
                        </h5>
                        <p className="text-xs text-muted-foreground mb-3">
                          When you choose a third-party service, your interaction with that service
                          is governed by its own privacy policy. PlugMonkey is not a party to that
                          relationship beyond routing your request from the extension to the API
                          host you configured.
                        </p>
                        <ul className="space-y-1 text-xs list-disc list-outside ml-5">
                          {ext.thirdPartyLinks.map((l, i) => (
                            <li key={i}>
                              <a
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                              >
                                {l.name}
                                <span className="sr-only">(opens in new tab)</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}
